import { ImportOutlined } from '@ant-design/icons';
import { Breakpoint, Button } from 'antd';
import { ColumnsType } from 'antd/es/table';
import classNames from 'classnames/bind';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { ReactComponent as FilterIcon } from '~/assets/icons/filter.svg';
import { FORMAT_DATE_WITH_HOUR } from '~/constants/common';
import PATH from '~/constants/path';
import { QuestionnaireResponse, QuestionnaireStatus } from '~/models/common';
import { RootState } from '~/store/store';
import { formatDate } from '~/utils/utils';
import styles from './index.module.scss';

interface ColumnsProps {
  isCompany?: boolean;
  isQuestionnaireScreen: boolean;
  handleDeleteQuestionnaire: (id: string) => void;
  handleExportQuestionnaire?: (id: string) => void;
}

const cx = classNames.bind(styles);

export const columns = ({
  isCompany,
  isQuestionnaireScreen,
  handleDeleteQuestionnaire,
  handleExportQuestionnaire,
}: ColumnsProps): ColumnsType<QuestionnaireResponse> => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const userInfo = useSelector((state: RootState) => state.auth.userInfo);

  const navigateToUserAnswers = (questionnaireId): void => {
    if (isQuestionnaireScreen) {
      navigate(
        PATH[isCompany ? 'company' : 'system'].questionnaire.detail(
          questionnaireId,
        ),
      );
    } else {
      navigate(
        PATH[isCompany ? 'company' : 'system'].healthCheck.listUserAnswer(
          questionnaireId,
        ),
      );
    }
  };
  const responsive = ['sm'] as Breakpoint[];

  const cols = [
    {
      title: (
        <span>
          {isQuestionnaireScreen
            ? t('questionnaire.list.title')
            : t('healthCheck.list.tableTitle')}
        </span>
      ),
      responsive,
      key: 'title',
      dataIndex: 'title',
      sorter: true,
    },
    {
      title: <span>{t('questionnaire.list.createdAt')}</span>,
      responsive,
      key: 'createdAt',
      sorter: true,
      align: 'left' as 'left',
      render: (record: QuestionnaireResponse): React.ReactElement => {
        return (
          <span>{formatDate(record.createdAt!, FORMAT_DATE_WITH_HOUR)}</span>
        );
      },
    },
    {
      title: <span>{t('questionnaire.list.status')}</span>,
      responsive,
      key: 'status',
      align: 'left' as 'left',
      filters: [
        {
          text: <span>{t('questionnaire.list.public')}</span>,
          value: QuestionnaireStatus.Public,
        },
        {
          text: <span>{t('questionnaire.list.unpublic')}</span>,
          value: QuestionnaireStatus.Unpublic,
        },
      ],
      filterIcon: <FilterIcon />,
      onFilter: (
        value: string | number | boolean,
        record: QuestionnaireResponse,
      ): boolean => {
        return value === record.status;
      },
      render: (record: QuestionnaireResponse): React.ReactNode => {
        switch (record.status as QuestionnaireStatus) {
          case QuestionnaireStatus.Public:
            return <span>{t('questionnaire.list.public')}</span>;
          case QuestionnaireStatus.Unpublic:
            return <span>{t('questionnaire.list.unpublic')}</span>;
          default:
            return <></>;
        }
      },
    },
    {
      title: <span>{t('questionnaire.list.author')}</span>,
      responsive,
      key: 'createdBy',
      dataIndex: 'createdBy',
      sorter: true,
    },
    {
      title: <span>{t('questionnaire.list.numOfUser')}</span>,
      responsive,
      key: 'numOfUser',
      dataIndex: 'numOfUser',
      align: 'center' as 'center',
      sorter: true,
    },

    ...((isCompany && userInfo?.isPrimary && isQuestionnaireScreen
      ? [
          {
            title: <span>{t('questionnaire.list.csv')}</span>,
            responsive,
            key: 'numOfUser',
            sorter: false,
            align: 'center' as 'center',
            render: (items): React.ReactElement => {
              return (
                <ImportOutlined
                  onClick={(): any =>
                    items.id && handleExportQuestionnaire?.(items.id)
                  }
                  className={cx('export-icon')}
                />
              );
            },
          },
        ]
      : []) as ColumnsType<QuestionnaireResponse>),

    ...((!isQuestionnaireScreen
      ? [
          {
            title: <span>{t('questionnaire.list.report')}</span>,
            responsive,
            sorter: false,
            render: (record: QuestionnaireResponse): React.ReactElement => {
              return (
                <Link
                  to={PATH[
                    isCompany ? 'company' : 'system'
                  ].questionnaire.detail(record?.id!)}
                >
                  <Button type="primary">{t('questionnaire.list.list')}</Button>
                </Link>
              );
            },
          },
        ]
      : []) as ColumnsType<QuestionnaireResponse>),
    {
      title: (
        <span>
          {t(
            isQuestionnaireScreen
              ? 'questionnaire.list.report'
              : 'healthCheck.list.userList',
          )}
        </span>
      ),
      responsive,
      key: 'report',
      align: 'center' as 'center',
      sorter: false,
      render: (record: QuestionnaireResponse): React.ReactElement => {
        return (
          <Button
            type="primary"
            onClick={(): void => {
              navigateToUserAnswers(record?.id);
            }}
          >
            {t('questionnaire.list.list')}
          </Button>
        );
      },
    },
    {
      title: <span>{t('questionnaire.list.action')}</span>,
      responsive,
      key: 'action',
      sorter: false,
      align: 'center' as 'center',
      render: (record: QuestionnaireResponse): React.ReactElement => {
        return (
          <div className="flex justify-around">
            {
              <Button
                type="primary"
                onClick={(): void => {
                  navigate(
                    PATH[isCompany ? 'company' : 'system'][
                      isQuestionnaireScreen ? 'questionnaire' : 'healthCheck'
                    ].edit(record.id!),
                  );
                }}
              >
                {t('questionnaire.list.edit')}
              </Button>
            }
            <Button
              type="primary"
              onClick={(): void => {
                handleDeleteQuestionnaire(record.id!);
              }}
            >
              {t('questionnaire.list.delete')}
            </Button>
          </div>
        );
      },
    },
  ];

  /*
  if (isCompany && userInfo?.isPrimary && isQuestionnaireScreen) {
    cols.splice(5, 0, {
      title: <span>{t('questionnaire.list.csv')}</span>,
      responsive,
      key: 'numOfUser',
      sorter: false,
      align: 'center' as 'center',
      render: (items): React.ReactElement => {
        return (
          <ImportOutlined
            onClick={(): any =>
              items.id && handleExportQuestionnaire?.(items.id)
            }
            className={cx('export-icon')}
          />
        );
      },
    });
  }
   */
  return cols;
};
